import jsPDF from 'jspdf';
import qrcode from 'qrcode-generator';
import { dessinerLogo, versRgb } from './planningPdfCommun';
import { lignesHoraires, jourPdf, texteDecoupage, juryDuPassage } from './planningOrauxPdf';

/**
 * LES CONVOCATIONS DU PLANNING ORAUX — une lettre A4 par destinataire (lot PO.4b-1, D28, maquette révision 15).
 *
 *   · demandées pour que le MAÎTRE (de stage ou d'apprentissage) se libère et que l'APPRENTI ait une preuve ;
 *   · l'EN-TÊTE vient de l'Administration (Mon département → En-tête des documents) : logos, établissement,
 *     coordonnées, ville, signataire (nom + fonction ÉCRITS — jamais de signature), couleur du trait, pied ;
 *   · le TEXTE vient des modèles du département (étudiant / maître ; pour le maître, l'ouverture « membre du jury »
 *     ou « invité(e) » est choisie toute seule) ; l'ENCADRÉ est fixe, calculé depuis le planning ;
 *   · le maître ne reçoit que le passage de SON étudiant (D15) ; aucune photo ;
 *   · en bas à droite, le QR de VÉRIFICATION (jeton signé par le serveur, page publique sans donnée personnelle —
 *     PO.4b-2) et sa référence ; sans jeton (serveur injoignable), une case « QR » à la place, jamais un faux code.
 * Fabriqué dans le navigateur, jamais stocké (nature B).
 */

const L = 210, MX = 20, LARG = L - 2 * MX;
const TAILLE = 10, INTER = 5;

const min = (h) => { const m = /^(\d{1,2}):(\d{2})/.exec(String(h || '')); return m ? Number(m[1]) * 60 + Number(m[2]) : null; };
const hhmm = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
const prenomNom = (c) => [c.prenom, String(c.nom || '').toUpperCase()].filter(Boolean).join(' ');
const qualiteMaitre = (c) => (c.origine === 'alternance' ? 'maître d\'apprentissage' : 'maître de stage');

/**
 * LA CIVILITÉ (2026-10-16, l'usage français : « Madame, » / « Monsieur, » seul ; « Madame, Monsieur, » si inconnue) :
 * l'étudiant → `sexe` (SCODOC, M / F) ; le maître d'apprentissage → `maitre_civilite` du registre (texte LIBRE : seules
 * les formes reconnues comptent, jamais « Madame fgdfgdf ») ; le maître de stage → aucune (la fiche de liaison n'en a pas).
 */
export function civiliteDe(d) {
    const c = d.cand;
    if (d.type === 'etudiant') return /^f$/i.test(String(c.sexe || '').trim()) ? 'Madame' : /^m$/i.test(String(c.sexe || '').trim()) ? 'Monsieur' : null;
    const t = String(c.maitre_civilite || '').trim().toLowerCase().replace(/\.$/, '');
    if (['mme', 'madame', 'mlle', 'mademoiselle'].includes(t)) return 'Madame';
    if (['m', 'mr', 'monsieur'].includes(t)) return 'Monsieur';
    return null;
}

/** Le maître est-il membre du jury de son étudiant ? (il y a été ajouté par « Son maître », D23 — même règle que l'écran) */
function maitreAuJury(planning, c) {
    const ext = planning.jurys?.externes?.[c.id] || [];
    return !!c.maitre && ext.some((x) => String(x.nom || '').toLowerCase() === c.maitre.toLowerCase());
}

/**
 * Les destinataires, dans l'ordre de passage : chaque étudiant placé, et le maître de chaque stagiaire / apprenti placé.
 * @param {Object} choix { etudiants, maitres, jours_exclus }
 */
export function destinatairesConvocation(planning, { etudiants = true, maitres = true, jours_exclus = [] } = {}) {
    const pos = new Map((planning.colonnes || []).filter((c) => c.salle).map((c, i) => [c.id, i]));
    const places = (planning.candidats || [])
        .filter((c) => c.place && pos.has(c.colonne_id) && !jours_exclus.includes(c.jour))
        .sort((a, b) => (a.jour < b.jour ? -1 : a.jour > b.jour ? 1 : a.ligne - b.ligne || pos.get(a.colonne_id) - pos.get(b.colonne_id)));
    // D31 (PO.7) — « Plusieurs oraux le même jour » : UN destinataire par étudiant (et par maître), qui porte TOUS ses oraux
    // (`ps`, dans l'ordre de la journée) ; `cand` = son premier oral ; `id` = son plus PETIT numéro de passage, stable quand
    // la rotation change l'ordre (clé du journal, du QR, de la réponse du maître).
    if (planning.modele === 'multi') {
        const groupes = new Map();
        for (const c of places) { const k = c.etudiant_id ?? 'c' + c.id; if (!groupes.has(k)) groupes.set(k, []); groupes.get(k).push(c); }
        const out = [];
        for (const ps of groupes.values()) {
            const c = ps[0], id = Math.min(...ps.map((x) => x.id));
            if (etudiants) out.push({ cle: 'e' + id, id, type: 'etudiant', cand: c, ps, nom: prenomNom(c) });
            if (maitres && c.maitre && (c.origine === 'stage' || c.origine === 'alternance')) {
                out.push({ cle: 'm' + id, id, type: 'maitre', cand: c, ps, nom: c.maitre, qualite: qualiteMaitre(c), entreprise: c.entreprise || '', jury: ps.some((x) => maitreAuJury(planning, x)) });
            }
        }
        return out;
    }
    const out = [];
    for (const c of places) {
        if (etudiants) out.push({ cle: 'e' + c.id, id: c.id, type: 'etudiant', cand: c, nom: prenomNom(c) });
        if (maitres && c.maitre && (c.origine === 'stage' || c.origine === 'alternance')) {
            out.push({ cle: 'm' + c.id, id: c.id, type: 'maitre', cand: c, nom: c.maitre, qualite: qualiteMaitre(c), entreprise: c.entreprise || '', jury: maitreAuJury(planning, c) });
        }
    }
    return out;
}

/**
 * v92 (D34) « au début de la demi-journée » : le matin finit au DÉJEUNER — le début de la pause si elle commence entre
 * 11 h et 14 h, sinon midi. Matin : la première ligne du planning ; après-midi : la première ligne après la coupure.
 * ⚠️ JUMEAU de `debutDemiJournee` (backend/services/planningOrauxTextes.js).
 */
export function debutDemiJournee(planning, debut) {
    const lignes = lignesHoraires(planning), pd = planning.pause_debut ? min(planning.pause_debut) : null;
    const coupure = pd !== null && pd >= 11 * 60 && pd <= 14 * 60 ? pd : 12 * 60;
    // `fin` (D34 bis) : les HORAIRES du planning — le matin, la coupure ; l'après-midi, l'heure de fin du planning
    if (debut < coupure) return { t: lignes.length ? lignes[0] : debut, pour: 'pour la matinée', fin: coupure };
    const apres = lignes.filter((t) => t >= coupure), fin = min(planning.heure_fin || planning.heure_fin_max);
    return { t: apres.length ? apres[0] : debut, pour: 'pour l\'après-midi', fin: fin === null ? debut : fin };
}
/** Pour l'infobulle du réglage : « 08:00 le matin, 13:30 l'après-midi » (ou « pas d'après-midi ») pour ce planning. */
export function texteDemiJournees(planning) {
    const lignes = lignesHoraires(planning);
    if (!lignes.length) return 'aucun créneau';
    const m = lignes.filter((t) => debutDemiJournee(planning, t).pour === 'pour la matinée');
    const a = lignes.filter((t) => debutDemiJournee(planning, t).pour !== 'pour la matinée');
    return `${m.length ? hhmm(m[0]) + ' le matin' : 'pas de matin'}, ${a.length ? hhmm(a[0]) + ' l\'après-midi' : 'pas d\'après-midi'}`;
}

/**
 * Les variables d'une convocation (celles de l'éditeur de modèles). `modele` : { delai_min, mode_convocation } — un
 * nombre seul vaut « N min avant ». `pour_demi` : « pour la matinée » / « pour l'après-midi » (mode demi-journée).
 */
export function variablesConvocation(planning, d, entete, modele) {
    const delai = modele && typeof modele === 'object' ? modele.delai_min : modele;
    const demi = d.type === 'etudiant' && modele && typeof modele === 'object' && modele.mode_convocation === 'demi_journee';
    const c = d.cand, lignes = lignesHoraires(planning);
    const act = (planning.duree_presentation_min || 0) + (planning.duree_questions_min || 0) + (planning.duree_deliberation_min || 0);
    const deb = lignes[c.ligne];
    const col = (planning.colonnes || []).find((x) => x.id === c.colonne_id);
    const jour = jourPdf(c.jour);
    const civ = civiliteDe(d);
    const v = {
        salutation: civ || 'Madame, Monsieur', e: civ === 'Madame' ? 'e' : civ === 'Monsieur' ? '' : '(e)',
        jury: juryDuPassage(planning, c).join(', '),
        prenom_nom_etudiant: prenomNom(c), formation: c.formation || (planning.formations || []).map((f) => f.code).join(' · '),
        intitule_oral: planning.titre || 'Oral', date_oral: jour.charAt(0).toLowerCase() + jour.slice(1),
        heure_debut: deb === undefined ? '—' : hhmm(deb), heure_fin: deb === undefined ? '—' : hhmm(deb + act),
        heure_convocation: deb === undefined ? '—' : demi ? hhmm(debutDemiJournee(planning, deb).t) : hhmm(Math.max(0, deb - (Number(delai) || 0))),
        pour_demi: deb !== undefined && demi ? debutDemiJournee(planning, deb).pour : '',
        // D34 bis : la demi-journée du passage, dans les DEUX modes — convocation de l'étudiant seulement
        ...(d.type === 'etudiant' ? {
            heure_debut_demi_journee: deb === undefined ? '—' : hhmm(debutDemiJournee(planning, deb).t),
            heure_fin_demi_journee: deb === undefined ? '—' : hhmm(debutDemiJournee(planning, deb).fin)
        } : {}),
        salle: col?.salle?.code || '', decoupage: texteDecoupage(planning), acces: entete?.acces || '',
        contact: [entete?.courriel, entete?.telephone].filter(Boolean).join(' · '),
        date_edition: new Date().toLocaleDateString('fr-FR'), ville: entete?.ville || '',
        maitre: d.type === 'maitre' ? d.nom : (c.maitre || ''), qualite_maitre: qualiteMaitre(c), entreprise: c.entreprise || ''
    };
    // D31 : plusieurs oraux le même jour — la date et « présentez-vous à » du PREMIER, la fin du DERNIER, chaque oral listé
    if (d.ps && d.ps.length > 1) {
        const oraux = d.ps.map((x) => {
            const db = lignes[x.ligne], cx = (planning.colonnes || []).find((k) => k.id === x.colonne_id);
            return { activite: x.activite || '', debut: db === undefined ? '—' : hhmm(db), fin: db === undefined ? '—' : hhmm(db + act), salle: cx?.salle?.code || '', jury: juryDuPassage(planning, x).join(', ') };
        });
        v.oraux = oraux;
        v.heure_fin = oraux[oraux.length - 1].fin;
        // D34 bis : la demi-journée commence avec le PREMIER oral et finit avec celle du DERNIER
        const dl = lignes[d.ps[d.ps.length - 1].ligne];
        if (d.type === 'etudiant' && dl !== undefined) v.heure_fin_demi_journee = hhmm(debutDemiJournee(planning, dl).fin);
        v.salle = oraux.map((o) => `${o.salle} (${o.activite})`).join(', ');
        v.jury = [...new Set(d.ps.flatMap((x) => juryDuPassage(planning, x)))].join(', ');
        v.liste_oraux = oraux.map((o) => `${o.debut} – ${o.fin} : ${o.activite}, salle ${o.salle}`).join(' ; ');
    }
    return v;
}
export const VARIABLES_CONVOCATION = ['salutation', 'e', 'prenom_nom_etudiant', 'formation', 'intitule_oral', 'date_oral', 'heure_debut', 'heure_fin', 'heure_convocation',
    'salle', 'decoupage', 'acces', 'contact', 'date_edition', 'ville'];
export const VARIABLES_MAITRE = ['maitre', 'qualite_maitre', 'entreprise'];
export const VARIABLES_ETUDIANT = ['heure_debut_demi_journee', 'heure_fin_demi_journee'];   // D34 bis

/** Une variable inconnue reste visible telle quelle (règle des modèles de courriel). */
export const remplir = (t, v) => String(t || '').replace(/\{(\w+)\}/g, (m, k) => (v[k] !== undefined ? v[k] : m));

/** La référence d'une convocation (le QR et la page de vérification arrivent au lot PO.4b-2). */
export const referenceConvocation = (planning, d, codeDept) =>
    `PO-${codeDept || 'DEPT'}-${String(planning.id).padStart(3, '0')}-${d.type === 'maitre' ? 'M' : 'E'}${d.cand.id}`;

/**
 * Fabrique le PDF : une page (ou plus si le texte est long) par destinataire.
 * @returns {{ pdf, pages, convocations }}
 */
/**
 * Le QR code d'une adresse (PO.4b-2) : dessiné en carrés vectoriels (net à l'impression, aucune image dans le PDF).
 * Correction « M » (15 %) : lisible même un peu froissé ; une marge blanche de 2 modules autour.
 */
export function dessinerQr(pdf, texte, x, y, taille) {
    const qr = qrcode(0, 'M');
    qr.addData(texte); qr.make();
    const n = qr.getModuleCount(), m = taille / (n + 4);
    pdf.setFillColor(255, 255, 255); pdf.rect(x, y, taille, taille, 'F');
    pdf.setFillColor(15, 23, 42);
    for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (qr.isDark(r, c)) pdf.rect(x + (c + 2) * m, y + (r + 2) * m, m, m, 'F');
}

export function genererConvocations({ planning, destinataires, modeles, entete, logo = null, logoUniversite = null, codeDept = '', jetons = null, qr = dessinerQr, compresser = false }) {
    // `compresser` (PO.5b, 2026-10-16) : une convocation JOINTE à un courriel. Mesuré : sans compression, jsPDF range les logos
    // en clair — 438 Ko pour une page ; compressée (Flate), une fraction. L'aperçu et les bancs lisent le texte en clair : non.
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: !!compresser });
    const rgb = versRgb(entete?.couleur_filet || '#ea580c'), TRAIT = [rgb.r, rgb.g, rgb.b];
    const TEINTE = TRAIT.map((x) => Math.round(x + (255 - x) * 0.92));
    const FONCE = TRAIT.map((x) => Math.round(x * 0.75));
    let premiere = true;

    const enTete = () => {
        const lu = entete?.logo_universite !== false ? dessinerLogo(pdf, logoUniversite, MX, 12, 12, 32) : 0;
        dessinerLogo(pdf, logo, MX + (lu ? lu + 3 : 0), 12, 12, 32);
        pdf.setTextColor(15, 23, 42); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10.5);
        pdf.text(pdf.splitTextToSize(entete?.etablissement || '', 110)[0] || '', L - MX, 15.5, { align: 'right' });
        pdf.setFont('helvetica', 'normal'); pdf.setFontSize(7.5); pdf.setTextColor(100, 116, 139);
        pdf.text(pdf.splitTextToSize(entete?.adresse || '', 110)[0] || '', L - MX, 20, { align: 'right' });
        pdf.text([entete?.telephone, entete?.courriel].filter(Boolean).join(' · '), L - MX, 23.8, { align: 'right' });
        pdf.setDrawColor(...TRAIT); pdf.setLineWidth(0.6); pdf.line(MX, 29, L - MX, 29);
    };

    const pied = (ref, url) => {
        pdf.setDrawColor(226, 232, 240); pdf.setLineWidth(0.2); pdf.line(MX, 258, L - MX, 258);
        pdf.setFont('helvetica', 'normal'); pdf.setFontSize(7); pdf.setTextColor(148, 163, 184);
        pdf.text(pdf.splitTextToSize(entete?.pied || '', 100), MX, 263);
        const xq = L - MX - 22, yq = 261;
        if (qr && url) qr(pdf, url, xq, yq, 22);
        else {   // PO.4b-1 : la place du QR (le vrai code et la page de vérification arrivent au lot PO.4b-2)
            pdf.setDrawColor(148, 163, 184); pdf.setLineDashPattern([1, 1], 0); pdf.rect(xq, yq, 22, 22); pdf.setLineDashPattern([], 0);
            pdf.setFontSize(7); pdf.text('QR', xq + 11, yq + 12, { align: 'center' });
        }
        pdf.setFontSize(7); pdf.setTextColor(100, 116, 139);
        pdf.text(['Vérifier ce document :', 'scannez le code', 'Réf. ' + ref], xq - 3, yq + 8, { align: 'right' });
    };

    for (const d of destinataires) {
        const m = d.type === 'maitre' ? modeles.maitre : modeles.etudiant;
        const v = variablesConvocation(planning, d, entete, m);
        // le jeton (référence + adresse de vérification) vient du SERVEUR ; sans lui (hors ligne), la référence seule
        const jt = jetons?.[d.cle];
        const ref = jt?.ref || referenceConvocation(planning, d, codeDept);
        const url = jt?.url || null;
        if (!premiere) pdf.addPage();
        premiere = false;
        enTete();
        let y = 40;
        const place = (h) => { if (y + h > 252) { pied(ref, url); pdf.addPage(); enTete(); y = 40; } };
        const para = (texte) => {
            pdf.setFont('helvetica', 'normal'); pdf.setFontSize(TAILLE); pdf.setTextColor(30, 41, 59);
            for (const p of remplir(texte, v).split(/\n\s*\n/)) {
                const lignes = pdf.splitTextToSize(p.replace(/\n/g, ' ').trim(), LARG);
                place(lignes.length * INTER + 2);
                pdf.text(lignes, MX, y); y += lignes.length * INTER + 2.5;
            }
        };
        // LA LETTRE À LA FRANÇAISE (2026-10-16) : le destinataire À DROITE (étudiant : son nom seul ; maître : son nom et
        // son entreprise), la ville et la date dessous, puis l'objet ; l'ouverture dit « Madame, » / « Monsieur, »
        pdf.setFont('helvetica', 'bold'); pdf.setFontSize(TAILLE); pdf.setTextColor(15, 23, 42);
        pdf.text(d.nom, L - MX, y, { align: 'right' }); y += INTER;
        if (d.type === 'maitre' && v.entreprise) { pdf.setFont('helvetica', 'normal'); pdf.setTextColor(71, 85, 105); pdf.text(v.entreprise, L - MX, y, { align: 'right' }); y += INTER; }
        y += 4;
        pdf.setFont('helvetica', 'normal'); pdf.setTextColor(30, 41, 59);
        pdf.text(`${v.ville}, le ${v.date_edition}`, L - MX, y, { align: 'right' }); y += 11;
        pdf.setFont('helvetica', 'bold'); pdf.setTextColor(15, 23, 42);
        const objet = pdf.splitTextToSize('Objet : ' + remplir(m.objet, v), LARG);
        pdf.text(objet, MX, y); y += objet.length * INTER + 4;
        para(d.type === 'maitre' && !d.jury ? m.intro_invite : m.intro);
        // l'encadré fixe
        const lignesBoite = [];
        if (d.type === 'maitre') lignesBoite.push(['Étudiant(e)', v.prenom_nom_etudiant]);
        // dans l'ordre CHRONOLOGIQUE (2026-10-16) : on arrive avant de passer
        lignesBoite.push(['Date', v.date_oral], ['Présentez-vous à', v.heure_convocation + (v.pour_demi ? ' — ' + v.pour_demi : '')]);
        // D31 : plusieurs oraux le même jour — une ligne par oral (horaire · activité · salle · son jury pour l'étudiant)
        if (v.oraux) for (const o of v.oraux) lignesBoite.push([`${o.debut} – ${o.fin}`, `${o.activite} — salle ${o.salle}${d.type === 'etudiant' && o.jury ? ` — jury : ${o.jury}` : ''}`]);
        else {
            lignesBoite.push(['Horaire du passage', `${v.heure_debut} – ${v.heure_fin}`], ['Salle', v.salle]);
            // l'étudiant connaît son jury (D15) ; le maître ne le voit pas, sauf son propre rôle s'il en est
            if (d.type === 'etudiant' && v.jury) lignesBoite.push(['Jury', v.jury]);
        }
        if (d.type === 'maitre' && d.jury) lignesBoite.push(['Votre rôle', 'membre du jury']);
        if (v.decoupage) lignesBoite.push(['Déroulé', v.decoupage]);
        pdf.setFontSize(9.5);
        const valeurs = lignesBoite.map(([, val]) => pdf.splitTextToSize(String(val || ''), LARG - 52));
        const hBoite = valeurs.reduce((s, l) => s + l.length * 4.6, 0) + 5;
        place(hBoite + 4);
        y += 1;
        pdf.setFillColor(...TEINTE); pdf.setDrawColor(...TRAIT); pdf.setLineWidth(0.4);
        pdf.roundedRect(MX, y, LARG, hBoite, 1.5, 1.5, 'FD');
        let yb = y + 5.5;
        lignesBoite.forEach(([lib], i) => {
            pdf.setFont('helvetica', 'bold'); pdf.setTextColor(...FONCE); pdf.text(lib, MX + 4, yb);
            pdf.setFont('helvetica', 'normal'); pdf.setTextColor(30, 41, 59); pdf.text(valeurs[i], MX + 48, yb);
            yb += valeurs[i].length * 4.6;
        });
        y += hBoite + 6;
        if (d.type === 'etudiant' && m.consignes) para('Consignes : ' + m.consignes);
        para(m.fin);
        // le signataire, écrit (jamais de signature, D28)
        place(14);
        y += 4;
        pdf.setFont('helvetica', 'normal'); pdf.setFontSize(TAILLE); pdf.setTextColor(30, 41, 59);
        pdf.text(entete?.signataire_fonction || '', L - MX, y, { align: 'right' }); y += INTER;
        pdf.setFont('helvetica', 'bold'); pdf.text(entete?.signataire_nom || '', L - MX, y, { align: 'right' });
        pied(ref, url);
    }
    if (premiere) {   // aucun destinataire : la page le dit (sans elle, un aperçu vide passe pour une panne)
        enTete();
        pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10); pdf.setTextColor(148, 163, 184);
        pdf.text('Aucun destinataire : placez des passages, et cochez étudiants et / ou maîtres.', L / 2, 60, { align: 'center' });
    }
    return { pdf, pages: pdf.getNumberOfPages(), convocations: destinataires.length };
}
